import { reportPlatformError } from "$root/api/error-reporting";
import { platformToast } from "$root/notification-system/toast";
import { parallelizeWithAllSettled } from "$root/utils/promise";
import type { PortfolioLabContextProps } from "./ctx";
import { portfolioLabRepository } from "./repository";

/**
 * Deletes lab columns server-side — the single path for every removal (one column via the header
 * menu, all of them via "Clear lab"). Callers own the optimistic `investments$` write; this owns
 * the server side of it.
 *
 * Two things it guarantees that a bare `deleteVirtualInvestment` per uuid does not:
 *
 * - **The column stops computing.** Each column's in-flight metrics computes are aborted (see
 *   {@link import("./ctx").InvestmentComputes}): the charts they would refresh are going away. The
 *   delete goes THROUGH the investment's write lane, so it stays ordered behind any composition write
 *   still flushing for that uuid; the compute runs off that lane and never holds it up.
 * - **Failures are surfaced.** The removal is optimistic and never rolled back (the lab's standing
 *   contract — see `syncComposition`), so a failed DELETE means the client and server disagree:
 *   the user is toasted that the columns may come back, and the failure is reported.
 *
 * Never rejects, so callers may await it (the "Clear lab" confirm dialog does, to stay up until the
 * server has actually dropped every column) or fire-and-forget it.
 */
export async function deleteInvestments(ctx: PortfolioLabContextProps, virtualUuids: string[]): Promise<void> {
	if (virtualUuids.length === 0) {
		return;
	}
	// Abort every compute up front, synchronously: a concurrency-limited `parallelize` could
	// otherwise hold a uuid's abort back behind other deletes, leaving its compute running meanwhile.
	for (const virtualUuid of virtualUuids) {
		ctx.investmentComputes.abort(virtualUuid);
	}

	const results = await parallelizeWithAllSettled(
		virtualUuids.map(
			(virtualUuid) => () =>
				ctx.investmentWriteLanes.run(virtualUuid, () => portfolioLabRepository.deleteVirtualInvestment(virtualUuid)),
		),
	);

	const failures = results.flatMap((result) => (result.status === "rejected" ? [result.reason] : []));
	if (failures.length === 0) {
		return;
	}
	reportPlatformError(failures[0], "ERROR", "portfolio", {
		message: `Unable to delete ${failures.length} of ${virtualUuids.length} lab virtual investment(s)`,
	});
	platformToast({
		icon: "Icon-full-error",
		severity: "error",
		children:
			failures.length === 1
				? "Failed to remove a composition from the lab. It may reappear the next time you open the lab."
				: `Failed to remove ${failures.length} compositions from the lab. They may reappear the next time you open the lab.`,
	});
}
